import { ref, toRaw } from 'vue'
import { storeToRefs } from 'pinia'
import { debounce } from 'lodash-es'
import { logService } from '@nsftx/seven-client-retail-utils'
import type {
  DMAcceptorStatusResponse,
  DMAcceptorType,
  DMGetBlockersResponse
} from '@nsftx/systems-sdk'
import type {
  AcceptorAction,
  AcceptorsInitOptions,
  AcceptorsPromiseItem
} from '#src/types/AcceptorsTypes'
import AcceptorsHooksManager from './acceptorsHooksManager'
import deviceManagementMoneyService from './deviceManagementMoneyService'
import deviceManagementPocketService from './deviceManagementPocketService'
import { hasBlockersFromActedList, hasNonIgnorableBlockers } from './acceptorStartBlockers'
import type HookErrorResponse from '#src/types/HookErrorResponse'
import errorParser from '#src/common/error-handling/errorParser'
import DMAcceptorsError from '#src/classes/DMAcceptorsError'
import { useDeviceManagementAcceptorsStore } from '#src/stores/deviceManagementAcceptorsStore'

const LOG_PREFIX = '[deviceManagementAcceptorsService]'
const ACCEPTORS_CHANGE_STATE_DEBOUNCE_DELAY = 2000
const acceptorsStatus = ref<Record<DMAcceptorType, { status: boolean }>>({
  bill: {
    status: false
  },
  coin: {
    status: false
  }
})

let isCheckingInProgress = true
let areAcceptorsInitialized: boolean
let countOfTries: number = 0
let promisesArray: Array<AcceptorsPromiseItem> = []
let runPromisesTimeout: number
let initStateTimeout: number | undefined
let initialDetectionExecuted: boolean = false

const executePromises = (): void => {
  const isAnyStopAttempt = promisesArray.some((promise) => promise.actionName === 'STOP')

  promisesArray.forEach((promise) => {
    const context = {
      areAcceptorsInitialized,
      isAnyStopAttempt,
      actionName: promise.actionName
    }

    if (!areAcceptorsInitialized) {
      promise.reject(
        new DMAcceptorsError(
          'Acceptors on ready promise rejected',
          'DM_ACCEPTORS_INIT_ON_READY_REJECTED',
          {
            context
          }
        )
      )
    }

    if (isAnyStopAttempt) {
      if (promise.actionName === 'STOP') {
        promise.resolve()
      } else {
        promise.reject(
          new DMAcceptorsError(
            'Cannot execute START action because there is STOP attempt by other call',
            'DM_ACCEPTORS_INIT_ON_READY_REJECTED',
            {
              context
            }
          )
        )
      }
    } else {
      promise.resolve()
    }
  })

  promisesArray = []
}

const validateStartAcceptors = (): Promise<void> =>
  new Promise((resolve, reject) => {
    const params = { errorResponses: [] }

    AcceptorsHooksManager.callHook('BeforeAcceptorsStart', params)
      .then(() => {
        if (params.errorResponses.length) {
          logService.info(`${LOG_PREFIX} BeforeAcceptorsStart - Acceptors cannot be started`, {
            code: 'DM_BEFORE_ACCEPTORS_START_REJECTION',
            details: {
              errorResponses: params.errorResponses
            }
          })
          reject(params.errorResponses)
        } else {
          resolve()
        }
      })
      .catch(reject)
  })

const getAcceptorStartBlockers = (devices: DMAcceptorStatusResponse[]) => {
  const list = Array.isArray(devices) ? devices : []
  const billBlockers = list.find(({ device }) => device === 'bill')?.blockers
  const coinBlockers = list.find(({ device }) => device === 'coin')?.blockers

  return {
    bill: billBlockers ?? [],
    coin: coinBlockers ?? []
  }
}

const parseGetBlockersResponse = (response: DMGetBlockersResponse) => {
  const billBlockers = Array.isArray(response?.bill_blockers) ? response.bill_blockers : []
  const coinBlockers = Array.isArray(response?.coin_blockers) ? response.coin_blockers : []

  return { billBlockers, coinBlockers }
}

const handleAcceptorsChangeStateSuccess = (
  response: DMAcceptorStatusResponse[],
  /**
   * START - if true,
   * STOP - if false
   */
  acceptMoney: boolean,
  event: {
    resolve: () => void
    reject: (error: DMAcceptorsError) => void
  }
) => {
  const acceptorsStore = useDeviceManagementAcceptorsStore()
  const { isAcceptorsStartBlocked, acceptorStartBlockers } = storeToRefs(acceptorsStore)

  if (!acceptMoney) {
    logService.info(`${LOG_PREFIX} Message to STOP acceptors successfully sent.`, {
      code: 'DM_ACCEPTORS_CHANGE_ACTION_SUCCESS',
      details: {
        acceptMoney,
        blockers: toRaw(acceptorStartBlockers.value)
      }
    })

    event.resolve()

    return
  }

  const blockers = getAcceptorStartBlockers(response)

  acceptorsStore.setAcceptorStartBlockers(blockers)

  if (!isAcceptorsStartBlocked.value) {
    logService.info(`${LOG_PREFIX} Message to START acceptors successfully sent.`, {
      code: `DM_ACCEPTORS_CHANGE_ACTION_SUCCESS`,
      details: {
        acceptMoney
      }
    })
    event.resolve()

    return
  }

  logService.error(`${LOG_PREFIX} Message to START acceptors was not sent. Blockers are present.`, {
    code: 'DM_ACCEPTORS_CHANGE_ACTION_ERROR',
    details: {
      blockers: toRaw(acceptorStartBlockers.value)
    }
  })

  event.reject(
    new DMAcceptorsError(
      'Message to START acceptors was not sent. Blockers are present.',
      'DM_ACCEPTORS_START_BLOCKERS_EXIST',
      {
        context: {
          blockers: acceptorStartBlockers.value
        }
      }
    )
  )
}

const handleChangeAcceptorsState = (
  event: {
    resolve: () => void
    reject: (error: unknown) => void
  },
  acceptMoney: boolean
) => {
  const action = acceptMoney ? 'START' : 'STOP'

  if (isCheckingInProgress) {
    logService.debug(`${LOG_PREFIX} Checking are acceptors initialized. Waiting ...`, {
      code: 'DM_ACCEPTORS_INITIALIZATION_WAITING',
      details: {
        acceptMoney
      }
    })

    window.setTimeout(() => {
      changeAcceptorsState(event, acceptMoney)
    }, 1000)

    return
  }

  if (!areAcceptorsInitialized) {
    logService.error(`${LOG_PREFIX} Acceptors are not initialized. Could not ${action} them.`, {
      code: 'DM_ACCEPTORS_NOT_INITIALIZED',
      details: {
        acceptMoney
      }
    })
    return event.reject(
      new DMAcceptorsError('Acceptors are not initialized.', 'DM_ACCEPTORS_NOT_INITIALIZED')
    )
  }

  deviceManagementMoneyService
    .getBlockers()
    .then((response: DMGetBlockersResponse) => {
      const { billBlockers, coinBlockers } = parseGetBlockersResponse(response)

      logService.info(`${LOG_PREFIX} Received get blockers on state change response`, {
        code: 'DM_ACCEPTORS_GET_BLOCKERS_ON_INIT_SUCCESS',
        details: {
          billBlockers,
          coinBlockers
        }
      })

      const acceptorsStore = useDeviceManagementAcceptorsStore()
      acceptorsStore.setAcceptorStartBlockers({
        bill: [...billBlockers],
        coin: [...coinBlockers]
      })

      deviceManagementMoneyService.enableBlockerEventActions()

      const shouldSkipStart =
        hasBlockersFromActedList(billBlockers) ||
        hasBlockersFromActedList(coinBlockers) ||
        hasNonIgnorableBlockers(billBlockers) ||
        hasNonIgnorableBlockers(coinBlockers)

      if (acceptMoney && shouldSkipStart) {
        const message = `${LOG_PREFIX} Message to START acceptors was not sent. Blockers are present.`
        const code = 'DM_ACCEPTORS_INIT_CHANGE_ACTION_ERROR'

        logService.error(message, {
          code,
          details: {
            billBlockers,
            coinBlockers
          }
        })

        event.reject(
          new DMAcceptorsError(message, code, {
            context: {
              billBlockers,
              coinBlockers
            }
          })
        )
        return undefined
      }

      return deviceManagementMoneyService.changeAcceptorStatusAll(acceptMoney)
    })
    .then((response) => {
      if (!response) {
        return
      }

      handleAcceptorsChangeStateSuccess(response, acceptMoney, event)
    })
    .catch((error: unknown) => {
      deviceManagementMoneyService.enableBlockerEventActions()

      logService.error(`${LOG_PREFIX} Failed to send message to ${action} acceptors.`, {
        code: `DM_ACCEPTORS_${action}_ERROR`,
        details: {
          acceptMoney
        },
        ...errorParser.parseUpstream(error)
      })
      event.reject(error)
    })
}

const changeAcceptorsState = debounce(
  handleChangeAcceptorsState,
  ACCEPTORS_CHANGE_STATE_DEBOUNCE_DELAY
)

/**
 * @description This method calls all registered promises (those who are waiting for
 * acceptors to be ready) after acceptors initialization check has been finished.
 * If acceptors are initialized and initial detection has been run, all promises will be
 * executed as resolved. Otherwise, all promises will be executed as rejected.
 */
const onInitializationFinished = (actionName: AcceptorAction): Promise<void> => {
  if (!isCheckingInProgress && initialDetectionExecuted) {
    if (areAcceptorsInitialized) {
      return Promise.resolve()
    }

    const error = new DMAcceptorsError(
      'Cannot resolve ready call. Acceptors are not initialized.',
      'DM_ACCEPTORS_NOT_INITIALIZED',
      {
        context: {
          areAcceptorsInitialized,
          isCheckingInProgress,
          actionName
        }
      }
    )
    return Promise.reject(error)
  }

  const promise = new Promise<void>((resolve, reject) => {
    promisesArray.push({
      resolve,
      reject,
      actionName
    })
  })

  return promise
}

/**
 * @description Starts bill and coin acceptors.
 * Note: this method will be resolved only after acceptors
 * initialization check have been finished.
 */
const startAcceptors = (): Promise<void> =>
  new Promise((resolve, reject) => {
    validateStartAcceptors()
      .then(() => {
        changeAcceptorsState({ resolve, reject }, true)
      })
      .catch(reject)
  })

/**
 * @description
 * This method will be resolved only after acceptors initialization check have been finished.
 */
const stopAcceptors = (): Promise<void> =>
  new Promise((resolve, reject) => {
    changeAcceptorsState({ resolve, reject }, false)
  })

const detectInitialAcceptorsState = () => {
  if (!isCheckingInProgress) {
    executePromises()
    window.clearTimeout(runPromisesTimeout)
    initialDetectionExecuted = true
  } else {
    runPromisesTimeout = window.setTimeout(detectInitialAcceptorsState, 1100)
  }
}

const markAcceptorsInitializationAsFailed = (): void => {
  isCheckingInProgress = false
  areAcceptorsInitialized = false
  countOfTries = 0
  if (initStateTimeout) {
    window.clearTimeout(initStateTimeout)
  }
  executePromises()
}

const initPocketModuleAfterAcceptorsReady = () => {
  deviceManagementPocketService
    .initPocketModule()
    .then(() => {
      logService.info(`${LOG_PREFIX} Pocket module successfully initialized`, {
        code: 'DM_POCKET_INIT_SUCCESS'
      })
    })
    .catch((pocketErr: Error) => {
      logService.error(`${LOG_PREFIX}  Pocket initialization failed.`, {
        code: 'DM_POCKET_INITIALIZATION_ERROR',
        ...errorParser.parseUpstream(pocketErr)
      })
    })
}

const startAcceptorsInitializationStateChecker = (startAcceptorsOnInit: boolean) =>
  new Promise<void>((resolveInit, rejectInit) => {
    {
      isCheckingInProgress = true
      countOfTries += 1

      if (countOfTries > 60) {
        logService.error(`${LOG_PREFIX} Acceptors are not initialized.`, {
          code: 'DM_ACCEPTORS_INITIALIZATION_ERROR'
        })

        markAcceptorsInitializationAsFailed()
        return rejectInit(
          new DMAcceptorsError('Acceptors are not initialized', 'DM_ACCEPTORS_NOT_INITIALIZED')
        )
      }

      deviceManagementMoneyService
        .initMoneyModule()
        .then(() => {
          isCheckingInProgress = false
          window.clearTimeout(initStateTimeout)
          areAcceptorsInitialized = true

          deviceManagementMoneyService.registerMoneyEvents()

          const initializationAction: AcceptorAction = startAcceptorsOnInit ? 'START' : 'STOP'

          onInitializationFinished(initializationAction)
            .then(() => {
              if (startAcceptorsOnInit) {
                logService.info(
                  `${LOG_PREFIX} Starting acceptors after they have been initialized.`,
                  {
                    code: 'DM_ACCEPTORS_INITIALIZATION_START'
                  }
                )

                initPocketModuleAfterAcceptorsReady()

                startAcceptors()
                  .then(resolveInit)
                  .catch((error: Array<HookErrorResponse>) => {
                    logService.warn(
                      `${LOG_PREFIX} Acceptors start error detected on acceptors initialization.`,
                      {
                        errorDetails: error,
                        code: 'DM_START_ACCEPTORS_ERROR'
                      }
                    )

                    rejectInit(error)
                  })
              } else {
                logService.info(
                  `${LOG_PREFIX} Stopping acceptors after they have been initialized.`,
                  {
                    code: 'DM_ACCEPTORS_INITIALIZATION_STOP'
                  }
                )

                initPocketModuleAfterAcceptorsReady()

                stopAcceptors()
                  .then(resolveInit)
                  .catch((error: unknown) => {
                    logService.warn(
                      `${LOG_PREFIX} Acceptors stop error detected on acceptors initialization.`,
                      {
                        errorDetails: error,
                        code: 'DM_STOP_ACCEPTORS_ON_INIT_ERROR',
                        ...errorParser.parseUpstream(error)
                      }
                    )

                    rejectInit(error)
                  })
              }
            })
            .catch((err: DMAcceptorsError) => {
              logService.warn(`${LOG_PREFIX} Acceptors initialization error detected.`, {
                error: err,
                code: 'DM_ACCEPTORS_INITIALIZATION_FAILED'
              })

              rejectInit(err)
            })
        })
        .catch((err: Error) => {
          logService.error(`${LOG_PREFIX} Acceptors init money module fails.`, {
            code: 'DM_MONEY_INITIALIZATION_ERROR',
            ...errorParser.parseUpstream(err)
          })
          markAcceptorsInitializationAsFailed()

          rejectInit(
            new DMAcceptorsError(
              'Acceptors init money module fails',
              'DM_ACCEPTORS_NOT_INITIALIZED'
            )
          )
        })
    }
  })

const stopAcceptor = (acceptorToStop: DMAcceptorType) =>
  new Promise((resolve, reject) => {
    deviceManagementMoneyService
      .changeAcceptorStatus(acceptorToStop, 'stop')
      .then((response) => {
        if (response.status.started === false) {
          acceptorsStatus.value[acceptorToStop].status = false
        }

        resolve(response)
      })
      .catch(reject)
  })

function getAcceptorStatus(acceptor: DMAcceptorType): Promise<boolean> {
  return new Promise((resolve, reject) => {
    deviceManagementMoneyService
      .getAcceptorStatus(acceptor)
      .then(({ status: { started } }) => {
        acceptorsStatus.value[acceptor].status = started
        resolve(started)
      })
      .catch((err) => {
        logService.warn(`${LOG_PREFIX} Get acceptor status failed`, {
          ...errorParser.parseUpstream(err),
          code: 'DM_GET_ACCEPTOR_STATUS_FAILED',
          acceptor
        })
        acceptorsStatus.value[acceptor].status = false
        return reject(
          new DMAcceptorsError('Get acceptor status failed', 'DM_GET_ACCEPTOR_STATUS_FAILED', {
            cause: err
          })
        )
      })
  })
}

const init = (options?: AcceptorsInitOptions) =>
  startAcceptorsInitializationStateChecker(options?.startAcceptorsOnInit ?? true)

const cancelTransaction = (id: string) => {
  return deviceManagementPocketService.payinCancel(id)
}

const retryTransaction = (id: string) => {
  return deviceManagementPocketService.payinRetry(id)
}

const transferTerminalMoney = () => {
  return deviceManagementPocketService.emptyCashBox()
}

export default {
  init,
  acceptorsStatus,
  stopAcceptor,
  getAcceptorStatus,
  startAcceptors,
  stopAcceptors,
  onInitializationFinished,
  detectInitialAcceptorsState,
  validateStartAcceptors,
  cancelTransaction,
  retryTransaction,
  transferTerminalMoney,
  hasBlockersFromActedList,
  hasNonIgnorableBlockers
}
